/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – Dashboard.tsx
// Logo imagine sidebar + favicon dinamic la collapse
// ============================================================

import React, { useState, useEffect } from 'react';
import { Dropdown, Drawer, Tooltip } from 'antd';
import {
  DashboardOutlined, EnvironmentOutlined, AlertOutlined,
  SettingOutlined, LogoutOutlined,
  FileTextOutlined, TeamOutlined, BellOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined,
  CheckCircleOutlined, InfoCircleOutlined,
  ExperimentOutlined, ToolOutlined, LineChartOutlined,
  UserOutlined, SafetyOutlined, ApartmentOutlined,
} from '@ant-design/icons';

import { useAuth }             from '../context/AuthContext';
import { SimulatorProvider, useSimulatorData } from '../context/SimulatorContext';
import { Notification }        from '../types';
import { mockNotifications }   from '../data/mockData';

import OverviewPanel     from './sections/OverviewPanel';
import SensorPanel       from './sections/SensorPanel';
import AlertPanel        from './sections/AlertPanel';
import PredictionPanel   from './sections/PredictionPanel';
import ReportsPanel      from './sections/ReportsPanel';
import ConfigPanel       from './sections/ConfigPanel';
import UsersPanel        from './sections/UsersPanel';
import MaintenancePanel  from './sections/MaintenancePanel';
import AnalysisPanel     from './sections/AnalysisPanel';
import ProfilePanel      from './sections/ProfilePanel';
import WaterQualityPanel from './sections/WaterQualityPanel';
import CompliancePanel   from './sections/CompliancePanel';

const LS_NOTIF_KEY = 'simadca_notifications_v3';

type SectionKey =
  | 'overview' | 'sensors' | 'alerts' | 'analysis'
  | 'predictions' | 'reports' | 'water_quality' | 'compliance'
  | 'maintenance' | 'config' | 'users' | 'profile';

interface NavItem {
  key:    SectionKey;
  icon:   React.ReactNode;
  label:  string;
  roles:  string[];
  group?: string;
  badge?: 'alert';
}

const NAV: NavItem[] = [
  { key: 'overview',      icon: <DashboardOutlined />,  label: 'General',         roles: ['admin','operator','viewer'], group: 'MONITORIZARE' },
  { key: 'sensors',       icon: <EnvironmentOutlined />, label: 'Senzori',         roles: ['admin','operator','viewer'] },
  { key: 'alerts',        icon: <AlertOutlined />,       label: 'Alerte',          roles: ['admin','operator','viewer'], badge: 'alert' },
  { key: 'analysis',      icon: <LineChartOutlined />,   label: 'Analiză',         roles: ['admin','operator','viewer'] },
  { key: 'water_quality', icon: <ApartmentOutlined />,   label: 'Calitate Apă',    roles: ['admin','operator','viewer'] },
  { key: 'predictions',   icon: <ExperimentOutlined />,  label: 'Predicții AI',    roles: ['admin','operator','viewer'], group: 'INTELIGENȚĂ' },
  { key: 'reports',       icon: <FileTextOutlined />,    label: 'Rapoarte',        roles: ['admin','operator','viewer'] },
  { key: 'compliance',    icon: <SafetyOutlined />,      label: 'Conformitate UE', roles: ['admin','operator','viewer'] },
  { key: 'maintenance',   icon: <ToolOutlined />,        label: 'Mentenanță',      roles: ['admin','operator'], group: 'ADMINISTRARE' },
  { key: 'config',        icon: <SettingOutlined />,     label: 'Configurare',     roles: ['admin','operator'] },
  { key: 'users',         icon: <TeamOutlined />,        label: 'Utilizatori',     roles: ['admin'] },
  { key: 'profile',       icon: <UserOutlined />,        label: 'Profilul meu',    roles: ['admin','operator','viewer'], group: 'CONT' },
];

const notifIcon = (type: Notification['type']) => {
  const s = { fontSize: 14 };
  switch (type) {
    case 'alert':  return <AlertOutlined    style={{ ...s, color: '#ef4444' }} />;
    case 'report': return <FileTextOutlined  style={{ ...s, color: '#6366f1' }} />;
    case 'system': return <InfoCircleOutlined style={{ ...s, color: '#f59e0b' }} />;
    default:       return <CheckCircleOutlined style={{ ...s, color: '#10b981' }} />;
  }
};

const loadNotifications = (): Notification[] => {
  try {
    const s = localStorage.getItem(LS_NOTIF_KEY);
    return s ? JSON.parse(s) : mockNotifications;
  } catch { return mockNotifications; }
};

// ─── Favicon helper ──────────────────────────────────────
// Când sidebar-ul e collapsed afișăm un favicon mic cu „S"
// Când e deschis revenim la favicon-ul normal din /public

const setFavicon = (collapsed: boolean) => {
  try {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    // Indiferent de starea sidebar-ului folosim logo-ul proiectului
    link.href = collapsed ? '/logo_simadca.png' : '/favicon.ico';
  } catch {}
};

// ─── DashboardInner ───────────────────────────────────────

const DashboardInner: React.FC = () => {
  const { user, logout, logActivity } = useAuth();
  const { sensors, alerts, lastUpdate, isRunning, pause, resume, newAlertIds } = useSimulatorData();

  const [section,   setSection]   = useState<SectionKey>('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs,    setNotifs]    = useState<Notification[]>(loadNotifications);

  // Persist notifications
  useEffect(() => {
    try { localStorage.setItem(LS_NOTIF_KEY, JSON.stringify(notifs.slice(0, 50))); } catch {}
  }, [notifs]);

  // Favicon dinamic la collapse
  useEffect(() => {
    setFavicon(collapsed);
  }, [collapsed]);

  // Auto-add notif din simulator
  useEffect(() => {
    if (!newAlertIds.length) return;
    newAlertIds.forEach(alertId => {
      const alert = alerts.find(a => a.id === alertId);
      if (!alert) return;
      const emoji = alert.severity === 'critical' ? '🔴' : alert.severity === 'high' ? '🟠' : '🟡';
      setNotifs(prev => {
        if (prev.find(n => n.id === `notif-${alertId}`)) return prev;
        return [{
          id: `notif-${alertId}`, type: 'alert' as const, read: false,
          title: `${emoji} ${alert.sensorName} — ${alert.parameter}`,
          message: `${alert.value.toFixed(3)} ${alert.unit} (LOD: ${alert.lod} ${alert.unit})`,
          timestamp: alert.timestamp,
        }, ...prev].slice(0, 50);
      });
    });
  }, [newAlertIds, alerts]);

  // Titlu pagină dinamic
  useEffect(() => {
    const label = allowed.find(i => i.key === section)?.label ?? 'General';
    document.title = `${label} — SIMADCA v3.0`;
    return () => { document.title = 'SIMADCA v3.0'; };
  }, [section]);

  const navigate = (key: SectionKey) => {
    setSection(key);
    logActivity(`Navigat la: ${NAV.find(n => n.key === key)?.label ?? key}`, 'Navigare');
  };

  const unread       = notifs.filter(n => !n.read).length;
  const activeAlerts = alerts.filter(a => a.status === 'active').length;
  const markAllRead  = () => setNotifs(p => p.map(n => ({ ...n, read: true })));
  const markRead     = (id: string) => setNotifs(p => p.map(n => n.id === id ? { ...n, read: true } : n));

  const allowed     = NAV.filter(i => user?.role && i.roles.includes(user.role));
  const roleColor   = user?.role === 'admin' ? '#6366f1' : user?.role === 'operator' ? '#10b981' : '#06b6d4';
  const currentLabel = allowed.find(i => i.key === section)?.label ?? 'General';

  const renderSection = () => {
    const map: Record<SectionKey, React.ReactNode> = {
      overview:     <OverviewPanel />,
      sensors:      <SensorPanel />,
      alerts:       <AlertPanel />,
      analysis:     <AnalysisPanel />,
      water_quality:<WaterQualityPanel />,
      predictions:  <PredictionPanel />,
      reports:      <ReportsPanel />,
      compliance:   <CompliancePanel />,
      maintenance:  <MaintenancePanel />,
      config:       <ConfigPanel />,
      users:        <UsersPanel />,
      profile:      <ProfilePanel />,
    };
    return map[section] ?? <OverviewPanel />;
  };

  const profileMenuItems = {
    items: [
      {
        key: 'info', disabled: true,
        label: (
          <div style={{ padding: '4px 0', minWidth: 190 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{user?.name}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{user?.email}</div>
            <span style={{ marginTop: 6, display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: `${roleColor}15`, color: roleColor }}>
              {user?.role?.toUpperCase()}
            </span>
          </div>
        ),
      },
      { type: 'divider' as const },
      { key: 'profile', icon: <UserOutlined />,   label: 'Profilul meu', onClick: () => navigate('profile') },
      { key: 'logout',  icon: <LogoutOutlined />, label: 'Deconectare',  danger: true, onClick: logout },
    ],
  };

  // ── Nav render ──
  const renderNav = () => {
    const items: React.ReactNode[] = [];
    let lastGroup = '';
    allowed.forEach(item => {
      // Label grup
      if (item.group && item.group !== lastGroup && !collapsed) {
        lastGroup = item.group;
        items.push(
          <div key={`g-${item.group}`} style={{
            fontSize: 10, fontWeight: 700, color: '#cbd5e1',
            letterSpacing: '0.08em', padding: '14px 20px 4px',
          }}>
            {item.group}
          </div>
        );
      }

      const active     = section === item.key;
      const badgeCount = item.badge === 'alert' ? activeAlerts : 0;

      items.push(
        <Tooltip key={item.key} title={collapsed ? item.label : ''} placement="right">
          <div
            onClick={() => navigate(item.key)}
            style={{
              display: 'flex', alignItems: 'center',
              gap: 10,
              padding: collapsed ? '10px 0' : '9px 12px',
              margin: collapsed ? '2px 10px' : '2px 8px',
              borderRadius: 10,
              justifyContent: collapsed ? 'center' : 'flex-start',
              cursor: 'pointer', position: 'relative',
              transition: 'all 0.15s ease',
              background: active ? '#eef2ff' : 'transparent',
            }}
            onMouseEnter={e => { if (!active) (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
            onMouseLeave={e => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
          >
            {/* Indicatorul activ */}
            {active && (
              <div style={{
                position: 'absolute', left: 0, top: '20%', bottom: '20%',
                width: 3, borderRadius: '0 3px 3px 0', background: '#6366f1',
              }} />
            )}

            {/* Icon */}
            <span style={{ fontSize: 16, flexShrink: 0, color: active ? '#6366f1' : '#94a3b8', transition: 'color 0.15s' }}>
              {item.icon}
            </span>

            {/* Label */}
            {!collapsed && (
              <span style={{
                fontSize: 13, fontWeight: active ? 700 : 500,
                color: active ? '#6366f1' : '#475569',
                flex: 1, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
                transition: 'color 0.15s',
              }}>
                {item.label}
              </span>
            )}

            {/* Badge alerte */}
            {!collapsed && badgeCount > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 6px',
                borderRadius: 99, background: '#fee2e2', color: '#ef4444', flexShrink: 0,
              }}>
                {badgeCount}
              </span>
            )}
          </div>
        </Tooltip>
      );
    });
    return items;
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>

      {/* ════ SIDEBAR ════ */}
      <aside style={{
        width: collapsed ? 68 : 240,
        minHeight: '100vh',
        background: '#ffffff',
        borderRight: '1px solid #e2e8f0',
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh',
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden', flexShrink: 0, zIndex: 100,
        boxShadow: '1px 0 0 #f1f5f9',
      }}>

        {/* ── Logo area ── */}
        <div style={{
          height: 64, display: 'flex', alignItems: 'center',
          padding: collapsed ? '0 10px' : '0 20px',
          borderBottom: '1px solid #f1f5f9',
          flexShrink: 0, overflow: 'hidden',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          {collapsed ? (
            <Tooltip title="SIMADCA" placement="right">
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: 18, color: '#fff',
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                cursor: 'default', userSelect: 'none',
                flexShrink: 0,
              }}>
                S
              </div>
            </Tooltip>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: 18, color: '#fff',
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                userSelect: 'none',
              }}>
                S
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', letterSpacing: '-0.3px', lineHeight: 1.2 }}>
                  SIMADCA
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '0.04em' }}>
                  v3.0 · BETA
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Nav items ── */}
        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto', overflowX: 'hidden' }}>
          {renderNav()}
        </nav>

        {/* ── User mini card (expanded only) ── */}
        {!collapsed && (
          <div style={{
            padding: '10px 14px', borderTop: '1px solid #f1f5f9',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${roleColor}, ${roleColor}99)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#fff',
            }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.name}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email}
              </div>
            </div>
          </div>
        )}

        {/* ── Collapse toggle ── */}
        <div style={{
          padding: '8px 12px',
          borderTop: collapsed ? '1px solid #f1f5f9' : 'none',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
        }}>
          <button
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Extinde meniu' : 'Îngustează meniu'}
            style={{
              padding: '6px 8px', borderRadius: 8,
              border: '1px solid #e2e8f0', background: '#f8fafc',
              cursor: 'pointer', color: '#64748b',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#eef2ff'; (e.currentTarget as HTMLButtonElement).style.color = '#6366f1'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'; (e.currentTarget as HTMLButtonElement).style.color = '#64748b'; }}
          >
            {collapsed
              ? <MenuUnfoldOutlined style={{ fontSize: 14 }} />
              : <MenuFoldOutlined   style={{ fontSize: 14 }} />}
          </button>
        </div>
      </aside>

      {/* ════ MAIN ════ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* ── Topbar ── */}
        <header style={{
          height: 64,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px',
          position: 'sticky', top: 0, zIndex: 90, flexShrink: 0,
        }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 500 }}>SIMADCA</span>
            <span style={{ color: '#e2e8f0', fontSize: 14 }}>/</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{currentLabel}</span>
          </div>

          {/* Dreapta */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

            {/* Live pill */}
            <Tooltip title={isRunning ? 'Click pentru pauză' : 'Click pentru reluare'}>
              <button
                onClick={() => isRunning ? pause() : resume()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 12px', borderRadius: 99, cursor: 'pointer',
                  background: isRunning ? '#f0fdf4' : '#f8fafc',
                  border: `1px solid ${isRunning ? '#bbf7d0' : '#e2e8f0'}`,
                  fontFamily: 'inherit', transition: 'all 0.15s',
                }}
              >
                <span style={{ position: 'relative', display: 'inline-block', width: 8, height: 8 }}>
                  {isRunning && (
                    <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#10b981', opacity: 0.4, animation: 'ping 1.5s ease infinite' }} />
                  )}
                  <span style={{ position: 'relative', display: 'block', width: 8, height: 8, borderRadius: '50%', background: isRunning ? '#10b981' : '#94a3b8' }} />
                  <style>{`@keyframes ping { 75%,100% { transform:scale(2); opacity:0; } }`}</style>
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: isRunning ? '#059669' : '#94a3b8' }}>
                  {isRunning ? 'LIVE' : 'PAUZAT'}
                </span>
              </button>
            </Tooltip>

            {/* Timestamp */}
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              <span style={{ opacity: 0.6 }}>Actualizat </span>
              {lastUpdate.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>

            {/* Bell notificări */}
            <Tooltip title="Notificări">
              <button
                onClick={() => setNotifOpen(true)}
                style={{
                  position: 'relative', width: 38, height: 38, borderRadius: 10,
                  border: '1px solid #e2e8f0', background: '#fff',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#64748b', fontSize: 16, transition: 'all 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#c7d2fe'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff';    (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0'; }}
              >
                <BellOutlined />
                {unread > 0 && (
                  <span style={{ position: 'absolute', top: 7, right: 7, width: 8, height: 8, borderRadius: '50%', background: '#ef4444', border: '2px solid #fff' }} />
                )}
              </button>
            </Tooltip>

            {/* Avatar dropdown */}
            <Dropdown menu={profileMenuItems} placement="bottomRight" trigger={['click']}>
              <button style={{
                width: 38, height: 38, borderRadius: '50%',
                background: `linear-gradient(135deg, ${roleColor}, ${roleColor}99)`,
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: '#fff',
                boxShadow: `0 2px 8px ${roleColor}40`,
              }}>
                {user?.name?.charAt(0).toUpperCase()}
              </button>
            </Dropdown>
          </div>
        </header>

        {/* ── Content ── */}
        <main style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
          {renderSection()}
        </main>
      </div>

      {/* ════ DRAWER NOTIFICĂRI ════ */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <BellOutlined style={{ color: '#6366f1' }} />
              <span style={{ fontWeight: 700 }}>Notificări</span>
              {unread > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: '#fee2e2', color: '#ef4444' }}>
                  {unread} necitite
                </span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                Marchează toate citite
              </button>
            )}
          </div>
        }
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        placement="right" width={360}
        styles={{ body: { padding: 0 } }}
      >
        {notifs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: '#94a3b8' }}>
            <BellOutlined style={{ fontSize: 32, display: 'block', margin: '0 auto 12px' }} />
            <div style={{ fontSize: 13, fontWeight: 600 }}>Nicio notificare</div>
          </div>
        ) : (
          notifs.map(n => (
            <div
              key={n.id}
              onClick={() => markRead(n.id)}
              style={{
                padding: '12px 14px', display: 'flex', gap: 12,
                cursor: 'pointer', margin: '4px 8px', borderRadius: 10,
                background: n.read ? '#fff' : '#eef2ff',
                border: `1px solid ${n.read ? '#f1f5f9' : '#c7d2fe'}`,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (n.read) (e.currentTarget as HTMLDivElement).style.background = '#fafafa'; }}
              onMouseLeave={e => { if (n.read) (e.currentTarget as HTMLDivElement).style.background = '#fff'; }}
            >
              <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: n.read ? '#f8fafc' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                {notifIcon(n.type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: n.read ? 500 : 700, color: '#0f172a', marginBottom: 2, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                  {!n.read && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', flexShrink: 0, marginTop: 4 }} />}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{n.message}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>
                  {new Date(n.timestamp).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))
        )}
      </Drawer>
    </div>
  );
};

// ─── Root ────────────────────────────────────────────────

const Dashboard: React.FC = () => (
  <SimulatorProvider>
    <DashboardInner />
  </SimulatorProvider>
);

export default Dashboard;